import { Client } from "./client.type";
import { DirectPaymentEnum, EventScopeEnum } from "./project/enums";
import { Requesting } from "./requesting.type";
import { Group, User } from "./user.type";

export interface Project {
  _id: string;
  mmCode: string;
  yearCode: string;

  status: ProjectStatus;
  client: Client;
  commercial: User;
  firstAttendance: User;
  secondAttendance: User;
  event: ProjectEvent;
  groups: ProjectGroup[];
  requestings: Requesting[];
  requester: ProjectRequester;
  totalEvent?: number;
  advanceValue?: number;
  directCustomerPayment?: number;
  customerBilling?: number;
  billingDeadline?: string;
  riskDraw?: boolean;
  paymentMethod?: string;
  debitNote?: string;
  logisticProviderValue?: number;
  logisticRevenueValue?: number;
  productionProviderValue?: number;
  productionRevenueValue?: number;
  airlineProviderValue?: number;
  airlineRevenueValue?: number;
  rsvpValue?: number;
  creativeValue?: number;
  feeValue?: number;
  observations?: string;

  lastUserUpdated: User;
  projectLastUserUpdated?: User;
  /** Confirmação da pendência "Dados do evento" da aba Overview (MMV-2681). */
  eventDataConfirmedAt?: string | null;
  eventDataConfirmedBy?: User | null;
  deletedAt: null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProjectStatus {
  _id: string;
  status: string;
  justification: string;
  estimatedDate: string;
  user: User;
  updatedAt: string;
}
export interface ProjectRequester {
  _id?: string;
  name: string;
  phone: string;
  email: string;
  client?: string;
}
export interface ProjectClient {
  clientSelected: Client;
  requestings: Requesting[];
}

export interface ProjectAssignTime {
  commercial: User;
  firstAttendance: User;
  secondAttendance?: User;
  groups: ProjectGroup[];
}
export interface ProjectGroup {
  _id?: string;
  group: Group;
  leader: User;
  firstCoordinator: User;
  secondCoordinator: User;
}

export interface ProjectEvent {
  _id?: string;
  name: string;
  type: string;
  customerBudgetObservations: string;
  local?: string;
  addressFixed?: boolean;
  eventFormat: string;
  metrics?: string;
  zip_code: string;
  city: string;
  number: string;
  state: string;
  country: string;
  complement: string;
  eventProfile?: string;
  audience?: string;
  numberOfPeople?: number;
  startDate: Date;
  endDate: Date;
  sequenceDays?: boolean;
  duration?: number;
  sameTimeEveryDay?: boolean;
  objective?: string;
  observationsEvent?: string;
  customerChallenge?: string;
  // briefing fields
  /** @deprecated Use eventScope instead. Legacy: true = INTERNATIONAL, false = NATIONAL */
  isInternational?: boolean;
  eventScope?: EventScopeEnum;
  hasCompetitor?: boolean;
  numberOfCompetitors?: number;
  briefingStartDate: Date;
  sendDate: Date;
  presentationDate?: Date;
  briefingObservations?: string;
  pfzCode?: string;
  mappCode?: string;
  cventCode?: string;

  // ELI LILLY specific fields
  eliLillyInternalOrder?: string;
  eliLillyCostCenter?: string;
  eliLillyPurchaseOrder?: string;
  eliLillyVemId?: string;
  eliLillyProduct?: string;
  eliLillyBuDepartment?: string;

  neighborhood?: string;

  // new fields
  customerBudget?: number;
  paymentTerm?: string;
  acceptAdvances?: boolean;
  advanceObservations?: string;
  hasDrawnRisk?: boolean;
  monthlyTax?: number;
  drawnRiskObservations?: string;
  /** @deprecated Use directPayment instead. Legacy: true = YES, false = NO */
  makesDirectPayments?: boolean;
  directPayment?: DirectPaymentEnum;
  directPaymentsObservations?: string;
  fee?: string;
  paymentMethod?: string;
  paymentMethodObservations?: string;
  hourInit?: string;
  hourEnd?: string;
  schedulleObservations?: string;
  hostingPeriods?: Array<{ inicio: string; fim: string }>;
  billingMethod?: string;
  exchangeDate?: string;
  exchangeCurrency?: string;
  exchangeValue?: number;
  managementFieldValues?: Record<string, string>;
}

export enum TabActive {
  INITIAL = 0,
  SUMMARY = 1,
  SERVICES = 2,
  AERIAL = 3,
  RSVP = 4,
  CRIATIVO = 5,
  ESTANDE = 6,
  BUDGET = 7,
  NEGOTIATION = 8,
  APPROVED = 9,
  STORAGE = 10,
  /**
   * Primeira aba da barra, mas com valor no fim do enum: os índices 0–10 já
   * são usados como número em outros pontos (ex.: `tabActive === 2`).
   */
  OVERVIEW = 11,
  /**
   * Aba Produção (Estudo de Verba). Também no fim do enum, para não deslocar
   * os índices que outros pontos comparam por número.
   */
  PRODUCAO = 12,
}

export interface ProjectFinance {
  totalEvent?: number;
  advanceValue?: number;
  directCustomerPayment?: number;
  customerBilling?: number;
  billingDeadline?: string;
  riskDraw?: boolean;
  paymentMethod?: string;
  debitNote?: string;
  logisticProviderValue?: number;
  logisticRevenueValue?: number;
  productionProviderValue?: number;
  productionRevenueValue?: number;
  airlineProviderValue?: number;
  airlineRevenueValue?: number;
  rsvpValue?: number;
  creativeValue?: number;
  feeValue?: number;
  observations?: string;
}

export interface CepData {
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
}

export interface ProjectSummary {
  _id: string;
  project: string;
  status: string;
  RSVP: number;
  airComission: number;
  airFee: number;
  airMarkup: number;
  communicationFee: number;
  directPaymentOfClients: number;
  logisticComission: number;
  logisticFee: number;
  logisticMarkup: number;
  observations: number;
  taxes: number;
  totalValueOfEvents: number;
  invoicedMM: number;
  totalFees: number;
  totalMarkupAndOverhead: number;
  totalRevenue: number;
  profitability: number;
  totalTaxes: number;
}

export interface ProjectSummaryContext {
  ON_APPROVAL: ProjectSummary;
  APPROVED: ProjectSummary;
  CLOSURE: ProjectSummary;
  AUDITED: ProjectSummary;
}
